import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/axios';

const LocationContext = createContext(null);

export const LocationProvider = ({ children }) => {
  const { user, effectiveRole } = useAuth();
  const [lastLocation, setLastLocation] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const [error, setError] = useState(null);

  // Configuration
  const HEARTBEAT_INTERVAL = 300000; // 5 minutes
  const HIGH_ACCURACY = true;

  useEffect(() => {
    let watchId;
    let intervalId;

    // Track when the user is operating in field mode — a real worker, or a
    // manager who toggled into Field view (effectiveRole resolves that)
    if (user && (effectiveRole || user.role) === 'worker') {
      startTracking();
    } else {
      stopTracking();
    }

    // Fresh fix the moment the tab re-surfaces — a backgrounded tab can't
    // heartbeat, so the return-to-foreground event is the capture moment.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(handlePosition, handleError,
          { enableHighAccuracy: HIGH_ACCURACY });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    function startTracking() {
      setTrackingActive(true);
      
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        return;
      }

      // 1. Immediate position
      navigator.geolocation.getCurrentPosition(
        handlePosition,
        handleError,
        { enableHighAccuracy: HIGH_ACCURACY }
      );

      // 2. Periodic Heartbeat (every 5 mins)
      intervalId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
            handlePosition,
            handleError,
            { enableHighAccuracy: HIGH_ACCURACY }
        );
      }, HEARTBEAT_INTERVAL);
    }

    function stopTracking() {
      setTrackingActive(false);
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (intervalId) clearInterval(intervalId);
    }

    function handlePosition(position) {
      const { latitude, longitude } = position.coords;
      const timestamp = new Date().toISOString();

      setLastLocation({ lat: latitude, lng: longitude, timestamp });
      setError(null);

      // Send to API
      sendLocationUpdate(latitude, longitude);
    }

    function handleError(err) {
      console.warn('Geolocation error:', err);
      setError(err.message);
    }

    async function sendLocationUpdate(lat, lng) {
        try {
            // Need employee ID. Usually tied to user.
            // Assuming the backend can infer employee from user or we store it in auth context.
            // If not, we might need to fetch employee profile first.
            // For now, assuming user.employee_id or we fetch it.
            
            // Actually, let's just send it. The backend uses `current_user` to verify access.
            // But the endpoint expects `employee_id`. 
            // We'll rely on the `user` object having it or fetching "my profile"
            
            // Don't gate on user.employee_id — it can be absent from older
            // tokens (the user->employee link is resolved server-side now).
            // Omit it and the endpoint resolves the caller's employee row.
            const payload = { latitude: lat, longitude: lng };
            if (user.employee_id) payload.employee_id = user.employee_id;
            await api.post('/worker/location', payload);
        } catch (err) {
            console.error('Failed to send location heartbeat:', err);
        }
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stopTracking();
    };
  }, [user, effectiveRole]);

  return (
    <LocationContext.Provider value={{ lastLocation, trackingActive, error }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => useContext(LocationContext);
